import React, { useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import {
    Checkbox,
    FormLabel,
    Grid,
    makeStyles,
    Typography,
    Button,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Select from 'components/widgets/Select';
import InventoryMap from 'services/mapData/InventoryMap';
import FeesPanel from 'components/modules/inventory/create/panels/FeesPanel';
import DisplayInput from 'components/modules/inventory/create/panels/DisplayInputNumber';
import DisplaySelect from 'components/modules/inventory/create/panels/DisplaySelect';
import DatePicker from 'react-datepicker';
import ButtonStyles from 'styles/theme/Button';
import CalendarContainer from 'components/widgets/form/CalendarContainer';
import PurchasingDialog from 'components/modules/inventory/create/dialogs/PurchasingDialog';
import ConfirmDialog from 'components/widgets/modal/ConfirmDialog';
import PurchasingElementActions from 'components/modules/inventory/create/PurchasingElementActions';

import StringUtils from 'lib/StringUtils';
import DateUtils from 'lib/DateUtils';
import { VehicleInfoProviders, NewUsedOptions } from 'utils/enum/InventoryEnum';

// icons
import getConditionReportOptions from 'components/modules/inventory/utils';

const buttonStyles = makeStyles((theme) => ButtonStyles.getStyle(theme));
const useStyles = makeStyles({
    root: {
        padding: 10,
        background: '#FBFBFB',
        marginBottom: 20,
        filter: 'brightness(98%)',
    },
    gridContainer: {
        padding: '5px 10px',
        '& > div': {
            alignItems: 'center',
            marginBottom: 5,
            paddingRight: 5,
            '& > div:first-child': {
                alignItems: 'center',
                paddingRight: 5,
            },
        },
    },
    datePicker: {
        '& > div:first-child': {
            width: '100%',
        },
        '& input': {
            maxHeight: 31,
        },
    },
});
const getProviders = (list) => {
    const { CAR_FAX, AUTO_CHECK } = VehicleInfoProviders;
    const carFaxOptions = [];
    const autoCheckOptions = [];

    list.forEach((item) => {
        if (item.type.toUpperCase() === CAR_FAX) {
            carFaxOptions.push({
                value: item.description,
                label: item.description,
            });
        }
        if (item.type.toUpperCase() === AUTO_CHECK) {
            autoCheckOptions.push({
                value: item.description,
                label: item.description,
            });
        }
    });

    return {
        carFaxOptions,
        autoCheckOptions,
    };
};

const PurchasingForm = ({
    data,
    loading,
    purchasedFromList,
    purchasingAgentList,
    providersOptions,
    onChange,
    onCreate,
    onEdit,
    onDelete,
    defaultReconCost,
}) => {
    const classes = { ...useStyles(), ...buttonStyles() };
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [state, setState] = useState({
        isPurchasingDialogOpen: false,
        isDeleteDialogOpen: false,
        title: '',
        field: '',
        currentAgent: null,
    });

    const invalidPurchasedFrom = StringUtils.isEmpty(data.purchasedFrom);
    const invalidPurchasedPrice = data.purchasedPrice <= 0;

    const toggleDeleteDialog = (field, id, name) => {
        setState({
            ...state,
            isDeleteDialogOpen: !state.isDeleteDialogOpen,
            currentAgent: { [field]: id, name },
        });
    };

    const openPurchasingDialog = (titleInput, fieldInput, currentAgent) => {
        setState({
            ...state,
            isPurchasingDialogOpen: true,
            title: titleInput,
            field: fieldInput,
            currentAgent,
        });
    };

    const purchasedFromOptions = [...purchasedFromList.map((item) => ({
        value: item.purchasedFrom,
        label: (
            <PurchasingElementActions
                itemId={item.purchasedFromId}
                label={item.purchasedFrom}
                onOpen={() => openPurchasingDialog('Edit Purchased From', 'purchasedFrom', item)}
                onToggle={() => toggleDeleteDialog('purchasedFromId', item.purchasedFromId, item.purchasedFrom)}
            />
        ),
    })), {
        label: (
            <Button
                className={classes.containedInfo}
                size="medium"
                fullWidth
                onClick={() => openPurchasingDialog('Add Purchased From', 'purchasedFrom')}
            >
                Add
            </Button>
        ),
        value: '-1',
        isDisabled: true,
    }];

    const purchasingAgentOptions = [...purchasingAgentList.map((item) => ({
        value: item.agent,
        label: (
            <PurchasingElementActions
                itemId={item.purchasingAgentsId}
                label={item.agent}
                onOpen={() => openPurchasingDialog('Edit Purchasing Agent', 'purchasedAgent', item)}
                onToggle={() => toggleDeleteDialog('purchasingAgentsId', item.purchasingAgentsId, item.agent)}
            />
        ),
    })), {
        label: (
            <Button
                className={classes.containedInfo}
                size="medium"
                fullWidth
                onClick={() => openPurchasingDialog('Add Purchasing Agent', 'purchasedAgent')}
            >
                Add
            </Button>
        ),
        value: '-1',
        isDisabled: true,
    }];

    const conditionReportOptions = getConditionReportOptions();
    const { carFaxOptions, autoCheckOptions } = getProviders(providersOptions);
    const onChangeForm = (fieldName, value, additionalInfo) => {
        onChange(additionalInfo ? `${fieldName}--${additionalInfo.entityMetadataId}` : fieldName, value, 'purchasingInfo');
    };

    const onClose = () => {
        setState({
            ...state,
            isPurchasingDialogOpen: false,
        });
    };

    const { totalPack } = InventoryMap.getTotalCost(data);

    return (
        <div className={clsx('d-flex-column', classes.root)}>
            <Typography variant="h5">Purchasing Information</Typography>
            <Grid
                container
                orientation="column"
                className={classes.gridContainer}
            >
                <Grid container item xs={12} sm={6}>
                    <Grid
                        container
                        item
                        wrap="nowrap"
                        className={isMobile ? '' : 'justify-end'}
                        xs={12}
                        sm={5}
                    >
                        <FormLabel
                            className="text-bold"
                        >
                            Purchased From:
                        </FormLabel>
                    </Grid>
                    <Grid item xs={12} sm={7}>
                        <Select
                            sorted
                            nowrap
                            size="sm"
                            name="purchasedFrom"
                            loading={loading}
                            options={purchasedFromOptions}
                            value={data.purchasedFrom}
                            className={clsx({ 'invalid-field': invalidPurchasedFrom })}
                            onChange={(name, value) => onChangeForm(name, value)}
                        />
                    </Grid>
                </Grid>
                <DisplayInput
                    label="Purchased Price"
                    value={data.purchasedPrice}
                    className={clsx({ 'invalid-field': invalidPurchasedPrice })}
                    onChange={(value) => onChangeForm('purchasedPrice', value)}
                    bold
                    alignRight={!isMobile}
                />
                <Grid container item xs={12} sm={6}>
                    <Grid
                        container
                        item
                        wrap="nowrap"
                        className={isMobile ? '' : 'justify-end'}
                        xs={12}
                        sm={5}
                    >
                        <FormLabel
                            className="text-bold"
                        >
                            Purchasing Agent:
                        </FormLabel>
                    </Grid>
                    <Grid item xs={12} sm={7}>
                        <Select
                            sorted
                            nowrap
                            size="sm"
                            name="purchasedAgent"
                            loading={loading}
                            options={purchasingAgentOptions}
                            value={data.purchasedAgent}
                            onChange={(name, value) => onChangeForm(name, value)}
                        />
                    </Grid>
                </Grid>
                <DisplayInput
                    label="Max Recon Cost"
                    value={data.maximumReconCost || defaultReconCost}
                    onChange={(value) => onChangeForm('maximumReconCost', value)}
                    bold
                    alignRight={!isMobile}
                    isReconCost
                />
                <Grid container item xs={12} sm={6}>
                    <Grid item xs={12} sm={5} className={clsx({ 'd-flex-justify-end': !isMobile })}>
                        <FormLabel className="text-bold">Purchased Date:</FormLabel>
                    </Grid>
                    <Grid item xs={12} sm={7} className={classes.datePicker}>
                        <DatePicker
                            size="small"
                            className="form-control form-control-sm"
                            onChange={(date) => onChangeForm('purchasedDate', date)}
                            selected={data.purchasedDate ? DateUtils.toLocal(data.purchasedDate).toDate() : ''}
                            popperContainer={CalendarContainer}
                            maxDate={DateUtils.toLocal(new Date()).toDate()}
                        />
                    </Grid>
                </Grid>
                <FeesPanel
                    bold
                    alignRight
                    totalPack={totalPack}
                    data={data}
                    onChange={onChangeForm}
                />
                <DisplaySelect
                    label="CR"
                    tooltip="Condition Report from Auction"
                    value={data.cr}
                    name="cr"
                    onChange={(value) => onChangeForm('cr', value)}
                    options={conditionReportOptions}
                    bold
                    alignRight={!isMobile}
                />
                <DisplayInput
                    label="Cost"
                    value={data.cost}
                    disabled
                    bold
                    alignRight={!isMobile}
                />
                <Grid container item xs={12} sm={6}>
                    <Grid item xs={12} sm={5} className={clsx({ 'd-flex-justify-end': !isMobile })}>
                        <FormLabel className="text-bold">Frame:</FormLabel>
                    </Grid>
                    <Grid item xs={12} sm={7}>
                        <Checkbox
                            checked={data.isFrame}
                            color="primary"
                            size="small"
                            onChange={(_, value) => onChangeForm('isFrame', value)}
                        />
                    </Grid>
                </Grid>
                <DisplaySelect
                    label="CarFax"
                    value={data.carFax}
                    name="carFax"
                    onChange={(value) => onChangeForm('carFax', value)}
                    options={carFaxOptions}
                    bold
                    alignRight={!isMobile}
                />
                <Grid container item xs={12} sm={6}>
                    <Grid
                        container
                        item
                        wrap="nowrap"
                        className={isMobile ? '' : 'justify-end'}
                        xs={12}
                        sm={5}
                    >
                        <FormLabel
                            className="text-bold"
                        >
                            New/Used:
                        </FormLabel>
                    </Grid>
                    <Grid item xs={12} sm={7}>
                        <Select
                            sorted
                            nowrap
                            size="sm"
                            name="newUsed"
                            loading={loading}
                            options={Object.values(NewUsedOptions)
                                .map((item) => ({
                                    value: item, label: item,
                                }))}
                            value={data.newUsed}
                            onChange={(name, value) => onChangeForm(name, value)}
                        />
                    </Grid>
                </Grid>
                <DisplaySelect
                    label="Auto Check"
                    value={data.autoCheck}
                    name="autoCheck"
                    onChange={(value) => onChangeForm('autoCheck', value)}
                    options={autoCheckOptions}
                    bold
                    alignRight={!isMobile}
                />
            </Grid>
            <PurchasingDialog
                isPurchasingDialogOpen={state.isPurchasingDialogOpen}
                title={state.title}
                field={state.field}
                current={state.currentAgent}
                onCreate={onCreate}
                onEdit={onEdit}
                onClose={onClose}
            />
            <ConfirmDialog
                title="Attention!"
                description={`Do you want to remove the purchasing ${state.currentAgent?.purchasingAgentsId ? 'agent' : 'from'}: ${state.currentAgent?.name}?`}
                open={state.isDeleteDialogOpen}
                variant="outlined"
                titlePrimary="Yes"
                titleSecondary="Cancel"
                onClose={() => toggleDeleteDialog()}
                onClickSecondary={() => toggleDeleteDialog()}
                onClickPrimary={() => {
                    const { currentAgent: { purchasedFromId, purchasingAgentsId } } = state;
                    const isPurchasingFrom = purchasedFromId > 0;

                    onDelete(isPurchasingFrom ? 'purchasedFrom' : 'purchasedAgent', isPurchasingFrom ? purchasedFromId : purchasingAgentsId);
                    toggleDeleteDialog();
                }}
            />
        </div>
    );
};

PurchasingForm.propTypes = {
    data: PropTypes.object,
    loading: PropTypes.bool,
    purchasedFromList: PropTypes.array,
    purchasingAgentList: PropTypes.array,
    providersOptions: PropTypes.array,
    onChange: PropTypes.func.isRequired,
    onCreate: PropTypes.func.isRequired,
    onEdit: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    defaultReconCost: PropTypes.number,
};

PurchasingForm.defaultProps = {
    data: {},
    loading: false,
    purchasedFromList: [],
    purchasingAgentList: [],
    providersOptions: [],
    defaultReconCost: 0,
};

export default PurchasingForm;
